import { __assign } from "tslib";
import React, { useEffect, useRef, useState } from 'react';
import Calendar from './calendar';
import { fireNonCancelableEvent } from '../internal/events';
import { InternalButton } from '../button/internal';
import TabTrap from '../date-picker/tab-trap';
import InternalBox from '../box/internal';
import InternalSegmentedControl from '../segmented-control/internal';
import SpaceBetween from '../space-between/index.js';
import segmentedControlStyles from '../segmented-control/styles.css.js';
import buttonTriggerStyles from '../internal/components/button-trigger/styles.css.js';
import styles from './styles.css.js';
import { RelativeRangePicker } from './relative-range';
import clsx from 'clsx';
import InternalAlert from '../alert/internal';
import { warnOnce } from '../internal/logging';
import { isDevelopment } from '../internal/is-development';
import { setTimeoffset } from './time-offset';
var VALID_RANGE = { valid: true };
function focusSegmentedControl(root) {
    root.getElementsByClassName(segmentedControlStyles.selected)[0].focus();
    var select = root.getElementsByClassName(buttonTriggerStyles['button-trigger']);
    for (var _i = 0, _a = Array.prototype.slice.call(select); _i < _a.length; _i++) {
        var button = _a[_i];
        button.focus();
    }
}
/**
 * This function fills in a start and end time if they are missing.
 */
function fillMissingTime(value) {
    if (!value) {
        return value;
    }
    var _a = value.startDate.split('T'), startDate = _a[0], startTime = _a[1];
    var _b = value.endDate.split('T'), endDate = _b[0], endTime = _b[1];
    return __assign(__assign({}, value), { startDate: startTime ? value.startDate : startDate + "T00:00:00", endDate: endTime ? value.endDate : endDate + "T23:59:59" });
}
function getDefaultMode(value, relativeOptions) {
    if (value && value.type) {
        return value.type;
    }
    if (relativeOptions.length > 0) {
        return 'relative';
    }
    return 'absolute';
}
export function DateRangePickerDropdown(_a) {
    var _b, _c;
    var _d = _a.locale, locale = _d === void 0 ? '' : _d, startOfWeek = _a.startOfWeek, isDateEnabled = _a.isDateEnabled, isValidRange = _a.isValidRange, value = _a.value, onChange = _a.onChange, onDropdownClose = _a.onDropdownClose, relativeOptions = _a.relativeOptions, showClearButton = _a.showClearButton, isSingleGrid = _a.isSingleGrid, i18nStrings = _a.i18nStrings, timeOffset = _a.timeOffset, timeInputFormat = _a.timeInputFormat;
    var applyButtonRef = useRef(null);
    var modeSwitcherRef = useRef(null);
    var validationErrorRef = useRef(null);
    var _e = useState(getDefaultMode(value, relativeOptions)), rangeSelectionMode = _e[0], setRangeSelectionMode = _e[1];
    var _f = useState((value === null || value === void 0 ? void 0 : value.type) === 'absolute' ? value : null), selectedAbsoluteRange = _f[0], setSelectedAbsoluteRange = _f[1];
    var _g = useState((value === null || value === void 0 ? void 0 : value.type) === 'relative' ? value : null), selectedRelativeRange = _g[0], setSelectedRelativeRange = _g[1];
    var _h = useState(false), applyClicked = _h[0], setApplyClicked = _h[1];
    var _j = useState(VALID_RANGE), validationResult = _j[0], setValidationResult = _j[1];
    var closeDropdown = function () {
        setApplyClicked(false);
        onDropdownClose();
    };
    var onClear = function () {
        closeDropdown();
        fireNonCancelableEvent(onChange, { value: null });
    };
    var onApply = function () {
        var _a;
        var visibleRange = rangeSelectionMode === 'relative' ? selectedRelativeRange : fillMissingTime(selectedAbsoluteRange);
        var newValidationResult = isValidRange(visibleRange);
        if (newValidationResult && newValidationResult.valid === false) {
            setApplyClicked(true);
            setValidationResult(newValidationResult);
            (_a = validationErrorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            var value_1 = visibleRange;
            if (isDevelopment) {
                if ((visibleRange === null || visibleRange === void 0 ? void 0 : visibleRange.type) === 'absolute') {
                    var startDateWithoutTime = visibleRange.startDate.split('T')[0];
                    var endDateWithoutTime = visibleRange.endDate.split('T')[0];
                    if (!startDateWithoutTime || !endDateWithoutTime) {
                        value_1 = null;
                        warnOnce('DateRangePicker', 'You have provided an `isValidRange` prop that did not catch a missing start or end date. This component will fall back to returning a `null` value instead of an incomplete one.');
                    }
                }
            }
            setApplyClicked(false);
            closeDropdown();
            var shiftedValue = setTimeoffset(value_1, timeOffset);
            fireNonCancelableEvent(onChange, { value: shiftedValue });
        }
    };
    useEffect(function () {
        if (applyClicked) {
            var visibleRange = rangeSelectionMode === 'relative' ? selectedRelativeRange : fillMissingTime(selectedAbsoluteRange);
            var newValidationResult = isValidRange(visibleRange);
            setValidationResult(newValidationResult || VALID_RANGE);
        }
    }, [applyClicked, isValidRange, rangeSelectionMode, selectedRelativeRange, selectedAbsoluteRange]);
    useEffect(function () {
        if (modeSwitcherRef.current) {
            focusSegmentedControl(modeSwitcherRef.current);
        }
    }, []);
    return (React.createElement("div", { className: clsx(styles.dropdown, (_b = {},
            _b[styles['one-grid']] = isSingleGrid,
            _b)), role: "dialog" },
        React.createElement(TabTrap, { focusNextCallback: function () { var _a; return (_a = applyButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } }),
        React.createElement(SpaceBetween, { size: "l" },
            React.createElement(InternalBox, { padding: { top: 'm', horizontal: 'l' } },
                React.createElement(SpaceBetween, { direction: "vertical", size: "s" },
                    React.createElement("div", { ref: modeSwitcherRef },
                        React.createElement(InternalSegmentedControl, { className: styles['mode-switch'], selectedId: rangeSelectionMode, options: [
                                { id: 'relative', text: i18nStrings.relativeModeTitle },
                                { id: 'absolute', text: i18nStrings.absoluteModeTitle },
                            ], onChange: function (e) {
                                setRangeSelectionMode(e.detail.selectedId);
                                setApplyClicked(false);
                                setValidationResult(VALID_RANGE);
                            } })),
                    rangeSelectionMode === 'absolute' && (React.createElement(Calendar, { isSingleGrid: isSingleGrid, initialEndDate: selectedAbsoluteRange === null || selectedAbsoluteRange === void 0 ? void 0 : selectedAbsoluteRange.endDate, initialStartDate: selectedAbsoluteRange === null || selectedAbsoluteRange === void 0 ? void 0 : selectedAbsoluteRange.startDate, locale: locale, startOfWeek: startOfWeek, isDateEnabled: isDateEnabled, i18nStrings: i18nStrings, onSelectDateRange: setSelectedAbsoluteRange, timeInputFormat: timeInputFormat })),
                    rangeSelectionMode === 'relative' && (React.createElement(RelativeRangePicker, { isSingleGrid: isSingleGrid, options: relativeOptions, initialSelection: selectedRelativeRange, onChange: function (range) { return setSelectedRelativeRange(range); }, i18nStrings: i18nStrings }))),
                React.createElement(InternalBox, { margin: !validationResult.valid ? { top: 's' } : undefined },
                    React.createElement("div", { "aria-live": "polite", tabIndex: -1, ref: validationErrorRef }, !validationResult.valid && (React.createElement(InternalAlert, { type: "error" },
                        React.createElement("span", { className: styles['validation-error'] }, validationResult.errorMessage)))))),
            React.createElement("div", { className: clsx(styles.footer, (_c = {},
                    _c[styles['one-grid']] = isSingleGrid,
                    _c[styles['has-clear-button']] = showClearButton,
                    _c)) },
                showClearButton && (React.createElement("div", { className: styles['footer-button-wrapper'] },
                    React.createElement(InternalButton, { onClick: onClear, className: styles['clear-button'], variant: "link", formAction: "none" }, i18nStrings.clearButtonLabel))),
                React.createElement("div", { className: styles['footer-button-wrapper'] },
                    React.createElement(SpaceBetween, { size: "xs", direction: "horizontal" },
                        React.createElement(InternalButton, { onClick: closeDropdown, className: styles['cancel-button'], variant: "link", formAction: "none" }, i18nStrings.cancelButtonLabel),
                        React.createElement(InternalButton, { onClick: onApply, className: styles['apply-button'], ref: applyButtonRef, formAction: "none" }, i18nStrings.applyButtonLabel))))),
        React.createElement(TabTrap, { focusNextCallback: function () { return modeSwitcherRef.current && focusSegmentedControl(modeSwitcherRef.current); } })));
}
//# sourceMappingURL=dropdown.js.map